import React, { useMemo } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useTheme } from "next-themes";

import {
  highlightPineLikeSource,
  PINE_SCRIPT_CODE_FONT,
} from "../../utils/pineScriptHighlight";
import {
  DEFAULT_MIN_SCRAMBLE_MS,
  usePineScriptScramble,
} from "./usePineScriptScramble";

/** One Dark–style tokens (dark editor panel). */
const HLJS_THEME_DARK: Record<string, React.CSSProperties | Record<string, unknown>> = {
  "&.hljs": { color: "#abb2bf", bg: "transparent" },
  "& .hljs-comment": { color: "#5c6370", fontStyle: "italic" },
  "& .hljs-keyword": { color: "#c678dd" },
  "& .hljs-selector-tag": { color: "#e06c75" },
  "& .hljs-built_in": { color: "#e6c07b" },
  "& .hljs-string": { color: "#98c379" },
  "& .hljs-number": { color: "#d19a66" },
  "& .hljs-literal": { color: "#56b6c2" },
  "& .hljs-symbol": { color: "#56b6c2" },
  "& .hljs-meta": { color: "#abb2bf" },
  "& .hljs-meta-keyword": { color: "#c678dd" },
  "& .hljs-title": { color: "#61afef" },
  "& .hljs-section": { color: "#e06c75" },
  "& .hljs-attribute": { color: "#98c379" },
  "& .hljs-doctag": { color: "#c678dd" },
  "& .hljs-name": { color: "#e06c75" },
  "& .hljs-type": { color: "#e6c07b" },
  "& .hljs-variable": { color: "#e06c75" },
  "& .hljs-template-variable": { color: "#e06c75" },
  "& .hljs-class .hljs-title": { color: "#e6c07b" },
  "& .hljs-function .hljs-title": { color: "#61afef" },
  "& .hljs-params": { color: "#abb2bf" },
  "& .hljs-regexp": { color: "#56b6c2" },
  "& .hljs-link": { color: "#56b6c2" },
  "& .hljs-subst": { color: "#e06c75" },
  "& .hljs-formula": { color: "#c678dd" },
  "& .hljs-emphasis": { fontStyle: "italic" },
  "& .hljs-strong": { fontWeight: 700 },
  "& .hljs-operator": { color: "#56b6c2" },
  "& .hljs-punctuation": { color: "#7f848e" },
  "& .hljs-property": { color: "#61afef" },
  "& .hljs-tag": { color: "#e06c75" },
  "& .hljs-attr": { color: "#d19a66" },
  "& .hljs-addition": { color: "#98c379" },
  "& .hljs-deletion": { color: "#e06c75" },
};

/** GitHub / VS Code–light–style tokens (light editor panel). */
const HLJS_THEME_LIGHT: Record<string, React.CSSProperties | Record<string, unknown>> = {
  "&.hljs": { color: "#24292f", bg: "transparent" },
  "& .hljs-comment": { color: "#6e7781", fontStyle: "italic" },
  "& .hljs-keyword": { color: "#cf222e" },
  "& .hljs-selector-tag": { color: "#116329" },
  "& .hljs-built_in": { color: "#8250df" },
  "& .hljs-string": { color: "#0a3069" },
  "& .hljs-number": { color: "#0550ae" },
  "& .hljs-literal": { color: "#0550ae" },
  "& .hljs-symbol": { color: "#0550ae" },
  "& .hljs-meta": { color: "#6e7781" },
  "& .hljs-meta-keyword": { color: "#cf222e" },
  "& .hljs-title": { color: "#6639ba" },
  "& .hljs-section": { color: "#116329" },
  "& .hljs-attribute": { color: "#0a3069" },
  "& .hljs-doctag": { color: "#cf222e" },
  "& .hljs-name": { color: "#116329" },
  "& .hljs-type": { color: "#953800" },
  "& .hljs-variable": { color: "#953800" },
  "& .hljs-template-variable": { color: "#953800" },
  "& .hljs-class .hljs-title": { color: "#953800" },
  "& .hljs-function .hljs-title": { color: "#6639ba" },
  "& .hljs-params": { color: "#24292f" },
  "& .hljs-regexp": { color: "#116329" },
  "& .hljs-link": { color: "#0550ae" },
  "& .hljs-subst": { color: "#953800" },
  "& .hljs-formula": { color: "#8250df" },
  "& .hljs-emphasis": { fontStyle: "italic" },
  "& .hljs-strong": { fontWeight: 700 },
  "& .hljs-operator": { color: "#0550ae" },
  "& .hljs-punctuation": { color: "#57606a" },
  "& .hljs-property": { color: "#0550ae" },
  "& .hljs-tag": { color: "#116329" },
  "& .hljs-attr": { color: "#0550ae" },
  "& .hljs-addition": { color: "#116329" },
  "& .hljs-deletion": { color: "#82071e" },
};

export type PineScriptCodeBlockTone = "default" | "master" | "active";

export type PineScriptScrambleConfig = {
  enabled: boolean;
  waitingForCode?: boolean;
  minScrambleMs?: number;
  onRevealComplete?: () => void;
  sessionKey?: string | number;
};

export type PineScriptCodeBlockProps = {
  code: string;
  /** Max height of the scrollable code panel */
  maxH?: string;
  /** Primary font + Tropical Surf on chrome titles ("Pinescript", "Generated code") */
  brandChrome?: boolean;
  /** Header bar label (left); defaults to "Pinescript" */
  chromeTitle?: string;
  /** Header bar label (right); defaults to "Generated code" */
  chromeSubtitle?: string;
  /**
   * Workspace dock tones:
   * - master → grey-500 chrome labels, light grey code surface
   * - active → tropical-surf chrome labels, alert-info blue code surface
   */
  tone?: PineScriptCodeBlockTone;
  /** Optional compose scramble inside this code block shell. */
  scramble?: PineScriptScrambleConfig;
};

/** Shared with strategy Pinescript view dialogs and detail panels */
export const PINE_SCRIPT_VIEW_MAX_H = "min(60vh, 520px)";

function resolveIsDarkTheme(resolvedTheme: string | undefined): boolean {
  if (resolvedTheme === "dark") return true;
  if (resolvedTheme === "light") return false;
  if (typeof document !== "undefined") {
    return (
      document.documentElement.classList.contains("dark") ||
      document.documentElement.dataset.theme === "dark"
    );
  }
  return false;
}

/**
 * Code panel for generated Pinescript with syntax colouring (light / dark).
 * Highlighting uses a JS grammar (Pine-like).
 */
export function PineScriptCodeBlock({
  code,
  maxH = "480px",
  brandChrome = false,
  chromeTitle = "Pinescript",
  chromeSubtitle = "Generated code",
  tone = "default",
  scramble,
}: PineScriptCodeBlockProps): React.ReactElement {
  const { resolvedTheme } = useTheme();
  const isLight = !resolveIsDarkTheme(resolvedTheme);

  const scrambleState = usePineScriptScramble({
    enabled: Boolean(scramble?.enabled),
    code,
    waitingForCode: scramble?.waitingForCode,
    minScrambleMs: scramble?.minScrambleMs ?? DEFAULT_MIN_SCRAMBLE_MS,
    onRevealComplete: scramble?.onRevealComplete,
    sessionKey: scramble?.sessionKey,
  });

  const isScrambling = Boolean(scramble?.enabled) && scrambleState.busy;
  const resolvedSubtitle =
    scramble?.enabled && scrambleState.busy
      ? scramble?.waitingForCode
        ? "Composing…"
        : scrambleState.phase === "revealing"
          ? "Decoding…"
          : chromeSubtitle
      : chromeSubtitle;

  const shellBorderDefault = isLight ? "gray.300" : "gray.600";
  const shellShadowDefault = isLight
    ? "0 1px 3px rgba(15, 23, 42, 0.08)"
    : "0 4px 20px rgba(0, 0, 0, 0.35)";
  /** Dark editor shells match workspace sidebar/card surfaces (not washed light grays). */
  const darkSidebarSurface = "var(--sidebar)";
  const darkMasterSurface = "color-mix(in oklab, var(--muted) 35%, var(--sidebar))";
  const darkActiveSurface =
    "color-mix(in oklab, var(--brand-tropical-surf) 16%, var(--sidebar))";
  const darkHeaderSurface = "color-mix(in oklab, var(--sidebar) 70%, #000)";

  const shellBgDefault = isLight ? "#ffffff" : darkSidebarSurface;
  const chromeBgDefault = isLight ? "gray.50" : darkHeaderSurface;
  const chromeBorderDefault = isLight ? "gray.200" : "whiteAlpha.100";
  const chromeTitleColorDefault = isLight ? "gray.700" : "gray.200";
  const chromeMutedColorDefault = isLight ? "gray.500" : "gray.400";
  const codePanelBgDefault = isLight ? "#f6f8fa" : darkSidebarSurface;

  const shellBorder = brandChrome ? "var(--input)" : shellBorderDefault;
  const shellShadow = brandChrome ? "none" : shellShadowDefault;
  const shellBg = brandChrome
    ? isLight
      ? undefined
      : darkSidebarSurface
    : shellBgDefault;
  const chromeBg = brandChrome
    ? isLight
      ? undefined
      : darkHeaderSurface
    : chromeBgDefault;
  const chromeBorder = brandChrome ? "var(--input)" : chromeBorderDefault;
  const chromeFontFamily = brandChrome ? "var(--font-primary)" : "'IBM Plex Sans', sans-serif";

  let chromeTitleColor = brandChrome ? "var(--brand-tropical-surf)" : chromeTitleColorDefault;
  let chromeMutedColor = brandChrome ? "var(--brand-tropical-surf)" : chromeMutedColorDefault;
  let codePanelBg = brandChrome
    ? isLight
      ? undefined
      : darkSidebarSurface
    : codePanelBgDefault;

  if (tone === "master") {
    chromeTitleColor = isLight ? "#6b7280" : "#ffffff";
    chromeMutedColor = chromeTitleColor;
    codePanelBg = isLight ? "#f3f4f6" : darkMasterSurface;
  } else if (tone === "active") {
    chromeTitleColor = "var(--brand-tropical-surf)";
    chromeMutedColor = "var(--brand-tropical-surf)";
    codePanelBg = isLight
      ? "color-mix(in oklab, var(--brand-tropical-surf) 10%, white)"
      : darkActiveSurface;
  }

  // Match shell to tone surface in dark so edge chrome is not a second mismatched colour.
  const resolvedShellBg =
    !isLight && tone === "master"
      ? darkMasterSurface
      : !isLight && tone === "active"
        ? darkActiveSurface
        : shellBg;

  const highlightedHtml = useMemo(
    () => (isScrambling ? "" : highlightPineLikeSource(code)),
    [code, isScrambling]
  );
  const fillParent = maxH === "100%";

  const codeSx = useMemo(
    () => {
      const scrollTrackBg =
        tone === "active"
          ? isLight
            ? "color-mix(in oklab, var(--brand-tropical-surf) 10%, white)"
            : darkActiveSurface
          : tone === "master"
            ? isLight
              ? "#f3f4f6"
              : darkMasterSurface
            : brandChrome
              ? isLight
                ? "#f6f8fa"
                : darkSidebarSurface
              : isLight
                ? "rgba(0,0,0,0.06)"
                : "rgba(0,0,0,0.22)";
      const scrollCornerBg = scrollTrackBg;

      return {
        // Always theme syntax colours so brandChrome / tone panels stay readable in dark mode.
        ...(isLight ? HLJS_THEME_LIGHT : HLJS_THEME_DARK),
        tabSize: 2,
        "&::-webkit-scrollbar": { w: "10px", h: "10px" },
        "&::-webkit-scrollbar-thumb": {
          bg: isLight ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.16)",
          borderRadius: "6px",
        },
        "&::-webkit-scrollbar-track": {
          bg: scrollTrackBg,
        },
        "&::-webkit-scrollbar-corner": {
          bg: scrollCornerBg,
        },
      };
    },
    [brandChrome, isLight, tone],
  );

  return (
    <Box
      data-pine-script-code-block=""
      {...(scramble?.enabled ? { "data-pine-script-scramble-reveal": "" } : {})}
      {...(scramble?.enabled ? { "data-pine-script-scramble-reveal-phase": scrambleState.phase } : {})}
      {...(brandChrome ? { "data-pine-script-code-block-brand": "" } : {})}
      {...(tone !== "default" ? { "data-pine-script-code-tone": tone } : {})}
      data-pine-script-surface=""
      borderRadius={brandChrome || tone !== "default" ? "8px" : "6px"}
      borderWidth="1px"
      borderColor={shellBorder}
      overflow="hidden"
      boxShadow={shellShadow}
      {...(fillParent ? { h: "100%", minH: 0, display: "flex", flexDirection: "column" } : {})}
      {...(resolvedShellBg != null
        ? { bg: resolvedShellBg }
        : tone === "master" || tone === "active"
          ? { bg: codePanelBg }
          : {})}
      aria-busy={isScrambling || undefined}
      aria-label={isScrambling ? "Composing Pinescript" : undefined}
    >
      <Flex
        data-pine-script-code-block-header=""
        align="center"
        justify="space-between"
        gap={3}
        px={3}
        py={2}
        flexShrink={0}
        {...(chromeBg != null ? { bg: chromeBg } : {})}
        borderBottomWidth="1px"
        borderColor={chromeBorder}
      >
        <Text
          data-pine-script-code-chrome-title=""
          fontSize="xs"
          fontWeight={brandChrome || tone !== "default" ? "normal" : "semibold"}
          letterSpacing={brandChrome || tone !== "default" ? "normal" : "0.04em"}
          color={chromeTitleColor}
          fontFamily={chromeFontFamily}
          textTransform={brandChrome || tone !== "default" ? "none" : "uppercase"}
          isTruncated
        >
          {chromeTitle}
        </Text>
        <Text
          data-pine-script-code-chrome-subtitle=""
          fontSize={brandChrome || tone !== "default" ? "xs" : "10px"}
          color={chromeMutedColor}
          fontFamily={chromeFontFamily}
          fontWeight={brandChrome || tone !== "default" ? "normal" : undefined}
          letterSpacing={brandChrome || tone !== "default" ? "normal" : "0.04em"}
          textTransform={brandChrome || tone !== "default" ? "none" : "uppercase"}
          flexShrink={0}
        >
          {resolvedSubtitle}
        </Text>
      </Flex>
      {isScrambling ? (
        <Box
          as="pre"
          data-pine-script-code-block-body=""
          role="region"
          aria-label="Pinescript composing preview"
          m={0}
          p={{ base: 3, md: 4 }}
          fontSize="13px"
          lineHeight={1.65}
          fontFamily={PINE_SCRIPT_CODE_FONT}
          {...(codePanelBg != null ? { bg: codePanelBg } : {})}
          whiteSpace="pre"
          overflow="auto"
          {...(fillParent ? { flex: 1, minH: 0 } : { maxH })}
          sx={codeSx}
        >
          {scrambleState.displayText}
        </Box>
      ) : (
        <Box
          as="pre"
          data-pine-script-code-block-body=""
          className="hljs"
          role="region"
          aria-label="Pinescript source"
          m={0}
          p={{ base: 3, md: 4 }}
          fontSize="13px"
          lineHeight={1.65}
          fontFamily={PINE_SCRIPT_CODE_FONT}
          {...(codePanelBg != null ? { bg: codePanelBg } : {})}
          whiteSpace="pre"
          overflow="auto"
          {...(fillParent ? { flex: 1, minH: 0 } : { maxH })}
          sx={codeSx}
          // Trusted server-generated Pine; highlighting produces escaped markup.
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
        />
      )}
    </Box>
  );
}
